import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to detect device type and screen size
 * Returns device information including type and orientation
 *
 * Uses threshold-based updates to prevent excessive re-renders
 * Only updates when device category or orientation actually changes
 */
const useDeviceDetect = () => {
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isPortrait: false,
    isLandscape: false,
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Store previous device category to detect actual changes
  const prevDeviceRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      // Breakpoints
      const isMobile = width < 768;
      const isTablet = width >= 768 && width < 1024;
      const isDesktop = width >= 1024;
      const isPortrait = height > width;
      const isLandscape = width > height;

      // Create new device info
      const newDeviceInfo = {
        isMobile,
        isTablet,
        isDesktop,
        isPortrait,
        isLandscape,
        width,
        height
      };

      // Only update if device category or orientation actually changed
      // This prevents re-renders during small resize events or orientation transitions
      const hasChanged =
        !prevDeviceRef.current ||
        prevDeviceRef.current.isMobile !== isMobile ||
        prevDeviceRef.current.isTablet !== isTablet ||
        prevDeviceRef.current.isDesktop !== isDesktop ||
        prevDeviceRef.current.isPortrait !== isPortrait ||
        prevDeviceRef.current.isLandscape !== isLandscape;

      if (hasChanged) {
        console.log('🔍 useDeviceDetect - Device state changed:', {
          width,
          height,
          isMobile,
          isPortrait,
          changed: hasChanged
        });
        setDeviceInfo(newDeviceInfo);
        prevDeviceRef.current = newDeviceInfo;
      } else {
        // Update width/height without triggering re-render
        // (dimensions changed but not device category/orientation)
        console.log('📏 useDeviceDetect - Size changed but category unchanged (no re-render)');
      }
    };

    // Initial detection
    handleResize();

    // Debounce resize events to prevent excessive calls during orientation change
    let resizeTimer;
    const debouncedHandleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResize, 150); // 150ms debounce
    };

    // Listen for resize events
    window.addEventListener('resize', debouncedHandleResize);

    // Cleanup
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  return deviceInfo;
};

export default useDeviceDetect;
